/*
 * FIX DE PRODUCCIÓN SOBRE EL 14/16 · ESTADOS HONESTOS DE LAS TARJETAS Y CIFRAS
 * DEL TABLERO (D-323, RN-622 a RN-624, RF-221).
 *
 * ====================================================================
 * POR QUÉ EXISTE ESTE ARCHIVO, Y POR QUÉ NO ES UN PARCHE DE UNA TARJETA
 * ====================================================================
 * En producción, el 2026-10-08, la cifra «Saldo de tus cuentas» pintó «Aún no
 * tienes ninguna cuenta activa» con 15 cuentas en la respuesta y el backend
 * absuelto (200, `error: null`, `totalSaldos` = 237 559.76). La causa real fue de
 * LECTURA: el Tablero leía la respuesta con una forma que el contrato no tiene
 * (ver `saldosPorMoneda` en agregacion.js), el resultado salió vacío y ese vacío
 * se pintó como «no tienes cuentas».
 *
 * La lección no es «arreglar ese campo»: es que CADA tarjeta decidía su estado por
 * su cuenta, con un `if` distinto, y en ninguna había un paso que preguntara «¿esta
 * respuesta es siquiera legible conforme al contrato?». Mientras ese paso no
 * exista, cualquier campo mal leído vuelve a terminar en un estado vacío falso.
 *
 * Aquí vive ese paso, UNA sola vez y para todas: `resolverEstadoDeTarjeta` decide
 * en qué estado está una tarjeta, y los lectores de abajo son los únicos que saben
 * qué forma tiene cada respuesta contratada.
 *
 * LOS CUATRO ESTADOS SON EXCLUYENTES (RN-622) y se nombran con el MISMO
 * vocabulario del núcleo (`ESTADO_DE_LISTADO`, D-83): no se inventa un segundo
 * juego de nombres para lo mismo.
 *   (a) CARGANDO  · se disparó la consulta y no hay respuesta ni fallo. Sin cifras,
 *                   sin ceros, sin «—» y sin mensajes de vacío (RN-141).
 *   (b) ERROR     · la consulta falló, O LA RESPUESTA NO SE PUDO LEER conforme al
 *                   contrato. Una 200 ilegible es ERROR, no vacío (RN-622b).
 *   (c) VACIO     · la respuesta llegó correcta y afirma cero registros.
 *   (d) CON_DATOS · la respuesta llegó con datos; se pintan tal cual (RN-600).
 *
 * RN-624 · Un dato ausente o ilegible NUNCA se degrada a cero ni a vacío. Por eso
 * ningún lector de este archivo usa `?? '0.00'` ni `?? []` para tapar un hueco:
 * un hueco devuelve ILEGIBLE y la tarjeta se va a (b).
 *
 * Es FUNCIÓN PURA, sin React y sin red, para que se pueda probar sin montar
 * pantalla.
 */

import { ESTADO_DE_LISTADO } from '../../../nucleo/listados/useConsultaDeListado.js';
import { casoDePresupuesto, CASOS_DE_PRESUPUESTO, saldosPorMoneda } from './agregacion.js';

/** Mismo vocabulario del núcleo, no uno paralelo (D-83, D-86). */
export const ESTADO_DE_TARJETA = ESTADO_DE_LISTADO;

/** Lo que devuelve un lector cuando la respuesta no corresponde al contrato. */
const ILEGIBLE = Object.freeze({ legible: false, vacio: false, valor: null });

/** Lo que devuelve un lector cuando la respuesta es correcta y afirma cero registros. */
function vacioDeVerdad(valor) {
  return { legible: true, vacio: true, valor };
}

function conDatos(valor) {
  return { legible: true, vacio: false, valor };
}

/**
 * EL RESOLVEDOR ÚNICO (RN-622). Entra el resultado de `useConsultaDeListado` y el
 * lector de esa respuesta; sale el estado y el valor ya leído.
 *
 * El orden de las preguntas NO es casual:
 *   1. ¿falló la consulta? → ERROR. Un fallo nunca se mira dos veces.
 *   2. ¿todavía no hay respuesta? → CARGANDO. Nunca se arranca en vacío (RN-141).
 *   3. ¿la respuesta es legible? → si no, ERROR. **Esta es la pregunta que faltaba
 *      y que dejó el defecto de producción pasar.**
 *   4. ¿afirma cero registros? → VACIO, el único estado donde el texto de vacío de
 *      la tarjeta puede aparecer (RN-623).
 *   5. en cualquier otro caso → CON_DATOS.
 *
 * @param consulta  `{ datos, error }` tal como lo entrega `useConsultaDeListado`.
 * @param leer      lector de la respuesta contratada de ESA tarjeta.
 * @returns `{ estado, valor, ilegible }` · `ilegible` distingue, para el registro
 *          técnico, un ERROR de transporte de un ERROR por forma inesperada. En
 *          pantalla los dos se ven igual: no se le explica al usuario la forma de
 *          una respuesta.
 */
export function resolverEstadoDeTarjeta(consulta, leer) {
  if (consulta?.error) {
    return { estado: ESTADO_DE_TARJETA.ERROR, valor: null, ilegible: false };
  }

  const datos = consulta?.datos;
  if (datos === null || datos === undefined) {
    return { estado: ESTADO_DE_TARJETA.CARGANDO, valor: null, ilegible: false };
  }

  const lectura = leer(datos);
  if (!lectura.legible) {
    return { estado: ESTADO_DE_TARJETA.ERROR, valor: null, ilegible: true };
  }
  if (lectura.vacio) {
    return { estado: ESTADO_DE_TARJETA.VACIO, valor: lectura.valor, ilegible: false };
  }
  return { estado: ESTADO_DE_TARJETA.CON_DATOS, valor: lectura.valor, ilegible: false };
}

// ---------------------------------------------------------------------------
// LOS LECTORES · lo único que sabe la forma de cada respuesta contratada
// ---------------------------------------------------------------------------

/**
 * ConsultarCuentas (2/6, RF-041) · `{ cuentas: [...], totalesPorMoneda: [...] }`.
 *
 * ESTA ES LA FORMA REAL DEL CONTRATO, y el defecto de producción nació de leer una
 * que no existe (`bloques`). Lo que se afirma, en este orden:
 *
 *   · `cuentas` tiene que ser un arreglo. Si no lo es, la respuesta no es la de
 *     este contrato: ILEGIBLE, no «cero cuentas».
 *   · `cuentas` vacío con la consulta en `soloActivas=true` SÍ es el vacío de
 *     verdad: no hay ninguna cuenta activa (RN-611).
 *   · Con cuentas presentes, `totalesPorMoneda` TIENE que venir. El servidor lo
 *     deja vacío cuando no pudo calcular los acumulados (y lo avisa en
 *     `advertencias`): eso es «no pude leer el saldo», no «tu saldo es cero»
 *     (RN-624). Va a ERROR con su «Reintentar».
 */
export function leerSaldosDeCuentas(respuesta) {
  if (!respuesta || typeof respuesta !== 'object') return ILEGIBLE;
  if (!Array.isArray(respuesta.cuentas)) return ILEGIBLE;

  if (respuesta.cuentas.length === 0) return vacioDeVerdad([]);

  if (!Array.isArray(respuesta.totalesPorMoneda) || respuesta.totalesPorMoneda.length === 0) {
    return ILEGIBLE;
  }

  const saldos = saldosPorMoneda(respuesta);
  // Defensa de forma: hay cuentas y hay totales, así que tiene que salir al menos
  // un renglón. Si no sale, algo de la forma cambió y no se finge un vacío.
  if (saldos.length === 0) return ILEGIBLE;

  return conDatos(saldos);
}

/**
 * ConsultarMovimientos (3/6) · los totales del mes por moneda.
 *
 * `totalesPorMoneda` ausente o no-arreglo es ILEGIBLE. Vacío de verdad es el mes
 * sin ningún movimiento en ninguna moneda (RN-604): ahí sí el servidor afirma cero.
 */
export function leerTotalesDelMes(respuesta) {
  if (!respuesta || typeof respuesta !== 'object') return ILEGIBLE;
  if (!Array.isArray(respuesta.totalesPorMoneda)) return ILEGIBLE;
  if (respuesta.totalesPorMoneda.length === 0) return vacioDeVerdad([]);
  return conDatos(respuesta.totalesPorMoneda);
}

/**
 * ConsultarMovimientos (3/6) · los renglones de «Últimos movimientos» (T-4).
 * Lista ausente = ILEGIBLE; lista vacía = mes sin renglones, y entonces T-4 no se
 * dibuja (RN-610).
 */
export function leerRenglonesRecientes(respuesta) {
  if (!respuesta || typeof respuesta !== 'object') return ILEGIBLE;
  if (!Array.isArray(respuesta.movimientos)) return ILEGIBLE;
  if (respuesta.movimientos.length === 0) return vacioDeVerdad([]);
  return conDatos(respuesta.movimientos);
}

/**
 * ConsultarPresupuestoDelMes (5/6) · LA TARJETA de presupuesto (T-2).
 *
 * [AÑADIDO en la vuelta del 2026-10-08 · Deisy Paez: «incorpórala».] T-2 era la
 * única exenta de la regla general y con eso quedaba viva la confusión que el FIX
 * persigue: `casoDePresupuesto` devuelve SIN_PRESUPUESTO cuando no encuentra
 * `lineas`, así que una respuesta ilegible terminaba diciendo «aún no defines
 * presupuesto» — un error disfrazado del vacío, RN-623 otra vez.
 *
 * LO QUE SE AFIRMA, y es lo único nuevo:
 *   · `lineas` tiene que ser un arreglo. Si no lo es, la respuesta no es la de
 *     este contrato: ILEGIBLE, no «no hay presupuesto».
 *   · `lineas` vacío ES el vacío de verdad: el mes no tiene presupuesto definido
 *     (RN-607). **Este caso no cambia en nada** y sigue invitando a definirlo,
 *     que es lo que hoy ve Deisy Paez en octubre y lo que debe seguir viendo.
 *   · Cada renglón tiene que traer su `claveEstado`. Es el campo con el que
 *     `lineasQueAvisan` decide quién avisa: un renglón sin él se caería del
 *     filtro en silencio y la tarjeta diría «todas tus categorías están en
 *     rango» — una tranquilidad inventada sobre un dato que no se pudo leer
 *     (RN-624). Mejor decir que no se pudo cargar.
 *
 * NO DECIDE NADA MÁS. Cuál de los dos casos con datos corresponde
 * —TODO_EN_RANGO o CON_AVISOS— lo sigue resolviendo `casoDePresupuesto` dentro
 * de la tarjeta, con D-312 intacta: solo las categorías que avisan y su enlace.
 */
export function leerLineasDelPresupuesto(respuesta) {
  if (!respuesta || typeof respuesta !== 'object') return ILEGIBLE;
  if (!Array.isArray(respuesta.lineas)) return ILEGIBLE;

  if (respuesta.lineas.length === 0) return vacioDeVerdad([]);

  const todasLegibles = respuesta.lineas.every(
    (linea) => typeof linea?.claveEstado === 'string' && linea.claveEstado !== '',
  );
  if (!todasLegibles) return ILEGIBLE;

  return conDatos(respuesta.lineas);
}

/**
 * ConsultarPresupuestoDelMes (5/6) · la cifra «Disponible de tu presupuesto».
 *
 * Esta lectura es la de la CIFRA DE CABECERA (T-5) y es DISTINTA de la de la
 * tarjeta, a propósito: la cifra necesita `totalDisponible` y la tarjeta no lo
 * usa nunca. Si se compartieran, un `totalDisponible` ausente apagaría una
 * tarjeta que no lo necesitaba, y eso sería apagar de más.
 *
 * `lineas` ausente es ILEGIBLE. Sin presupuesto del mes es vacío de verdad. Con
 * presupuesto pero sin `totalDisponible`, la cifra no llegó: ILEGIBLE, nunca «—»
 * de dato legítimo ni «$0.00» (RN-624).
 */
export function leerDisponibleDelPresupuesto(respuesta) {
  if (!respuesta || typeof respuesta !== 'object') return ILEGIBLE;
  if (!Array.isArray(respuesta.lineas)) return ILEGIBLE;

  if (casoDePresupuesto(respuesta) === CASOS_DE_PRESUPUESTO.SIN_PRESUPUESTO) {
    return vacioDeVerdad(null);
  }

  const disponible = respuesta.totalDisponible;
  if (disponible === null || disponible === undefined || disponible === '') return ILEGIBLE;

  return conDatos(disponible);
}

/**
 * ConsultarCuentas (2/6) · los totales por moneda crudos, para la pantalla que
 * presenta la deuda de tarjetas (RF-220, RN-625).
 *
 * `totalDeudas` se presenta TAL CUAL del servidor, sin recálculo, sin filtro propio
 * y sin resta contra nada. Si el total de esa moneda no vino, NO es cero: es dato
 * que no se pudo leer.
 */
export function leerTotalDeLaMoneda(respuesta, idMoneda) {
  if (!respuesta || !Array.isArray(respuesta.totalesPorMoneda)) return ILEGIBLE;

  const total = respuesta.totalesPorMoneda.find((t) => t.idMoneda === idMoneda);
  if (!total) return ILEGIBLE;
  if (total.totalSaldos === null || total.totalSaldos === undefined) return ILEGIBLE;
  if (total.totalDeudas === null || total.totalDeudas === undefined) return ILEGIBLE;

  return conDatos(total);
}
